import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEAPActivite5Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  const slides = [
    // Slide 1 - Titre
    {
      type: 'title',
      title: "Activité 5",
      subtitle: "Installation et aide aux déplacements - Ergonomie et prévention des TMS",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)"
    },
    // Slide 2 - Sommaire interactif
    {
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Ergonomie et TMS", items: ["Définition", "Risques en crèche", "Situations à risque"], color: "#10B981", slideTarget: 3 },
        { num: "2", title: "Principes de base", items: ["Gestes et postures", "Règles fondamentales"], color: "#3B82F6", slideTarget: 8 },
        { num: "3", title: "Techniques", items: ["Chevalier servant", "Le balancier", "Applications"], color: "#8B5CF6", slideTarget: 10 },
        { num: "4", title: "Matériel et prévention", items: ["Matériel facilitateur", "Rôle de l'AP", "Attendus DEAP/VAE"], color: "#F59E0B", slideTarget: 20 }
      ]
    },
    // Slide 3 - Section Ergonomie
    {
      type: 'section',
      title: "ERGONOMIE ET TMS",
      subtitle: "Comprendre pour mieux prévenir",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    // Slide 4 - Définition Ergonomie
    {
      type: 'content',
      title: "Définition : Ergonomie",
      definition: "Adapter le travail à l'être humain (et non l'inverse).",
      content: "L'ergonomie vise à réduire la fatigue, prévenir la douleur et les accidents tout en garantissant la sécurité de l'enfant et la qualité des soins.",
      extraInfo: [
        { term: "Responsabilité individuelle", desc: "Appliquer les bonnes postures au quotidien" },
        { term: "Responsabilité organisationnelle", desc: "Aménagement, matériel adapté, protocoles" }
      ]
    },
    // Slide 5 - TMS Définition
    {
      type: 'content',
      title: "Les TMS (Troubles Musculo-Squelettiques)",
      definition: "Atteintes des muscles, tendons, nerfs et articulations qui s'installent progressivement.",
      content: "Les premières douleurs sont un signal d'alerte. Il faut adapter ses gestes et alerter.",
      extraInfo: [
        { term: "Facteurs principaux", desc: "Gestes répétitifs, postures contraignantes, port de charge, contraintes prolongées" }
      ]
    },
    // Slide 6 - Zones touchées
    {
      type: 'list',
      title: "TMS - Zones souvent touchées",
      items: [
        { term: "Lombaires", desc: "Lombalgie, sciatique, hernie discale" },
        { term: "Cervicales", desc: "Douleurs de la nuque, tensions" },
        { term: "Épaules", desc: "Tendinites, douleurs articulaires" },
        { term: "Poignets", desc: "Syndrome du canal carpien" },
        { term: "Genoux", desc: "Usure articulaire, douleurs" }
      ],
      alertColor: "#EF4444",
      warning: "Les premières douleurs sont un signal → adapter et alerter"
    },
    // Slide 7 - Situations à risque
    {
      type: 'list',
      title: "Situations à risque en petite enfance",
      items: [
        { term: "Changes répétés", desc: "Flexions, rotations, bras tendus" },
        { term: "Portage / transferts", desc: "Sol → bras, bras → table, bras → lit" },
        { term: "Couchers / levers", desc: "Lits à barreaux profonds" },
        { term: "Activités au sol", desc: "Postures statiques, dos voûté" },
        { term: "Déplacements de matériel", desc: "Poussettes, lits, chariots" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 8 - Section Principes
    {
      type: 'section',
      title: "PRINCIPES DE BASE",
      subtitle: "Gestes et postures",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
    },
    // Slide 9 - Principes fondamentaux
    {
      type: 'steps',
      title: "Principes de base : gestes et postures",
      steps: [
        { num: "1", title: "Rapprocher", items: ["La charge (enfant) proche du corps", "Éviter les bras tendus"], color: "#10B981" },
        { num: "2", title: "Plier", items: ["Utiliser les cuisses", "Pas le dos"], color: "#3B82F6" },
        { num: "3", title: "Dos droit", items: ["Respecter les courbures naturelles", "Ne pas arrondir"], color: "#8B5CF6" },
        { num: "4", title: "Éviter", items: ["Les torsions", "Tourner avec les pieds"], color: "#F59E0B" },
        { num: "5", title: "Anticiper", items: ["Préparer le matériel", "Avant d'installer l'enfant"], color: "#EC4899" }
      ]
    },
    // Slide 10 - Section Techniques
    {
      type: 'section',
      title: "TECHNIQUES D'ERGONOMIE",
      subtitle: "Chevalier servant et balancier",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
    },
    // Slide 11 - Chevalier servant intro
    {
      type: 'contentLargeImage',
      title: "Posture du Chevalier Servant",
      definition: "Un genou au sol, l'autre fléchi. Dos droit, enfant au plus près du corps.",
      content: "Cette posture permet de se mettre à hauteur de l'enfant tout en protégeant son dos. Elle favorise également la relation à hauteur d'enfant.",
      extraInfo: [
        { term: "Utilisation", desc: "Habillage, aide au lever, jeu au sol, mise des chaussures" },
        { term: "Bénéfices", desc: "Moins de flexion du tronc + relation à hauteur d'enfant" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/t5k295mh_image.png"
    },
    // Slide 12 - Chevalier servant descente
    {
      type: 'steps',
      title: "Chevalier Servant - Pour descendre au sol",
      steps: [
        { num: "1", title: "Plier", items: ["Une jambe", "Poser un genou au sol"], color: "#3B82F6" },
        { num: "2", title: "Dos", items: ["Garder le dos droit", "Ne pas arrondir"], color: "#8B5CF6" },
        { num: "3", title: "Descendre", items: ["Contrôler le mouvement", "Souffler en descendant"], color: "#10B981" }
      ],
      note: "L'enfant reste proche du corps. Éviter toute flexion du tronc en avant."
    },
    // Slide 13 - Chevalier servant remontée
    {
      type: 'steps',
      title: "Chevalier Servant - Pour se relever",
      steps: [
        { num: "1", title: "Appui", items: ["Prendre appui sur la jambe avant", "Engager les cuisses"], color: "#3B82F6" },
        { num: "2", title: "Dos", items: ["Garder le dos droit", "Inspirer en remontant"], color: "#8B5CF6" },
        { num: "3", title: "Si besoin", items: ["Appui sur meuble sécurisé", "Ne jamais pousser sur le dos seul"], color: "#10B981" }
      ]
    },
    // Slide 14 - Phrase clé chevalier
    {
      type: 'keyMessage',
      title: "Phrase clé à retenir",
      messages: [
        "Je souffle en descendant, j'inspire en me relevant",
        "Je protège mon dos en gardant l'enfant proche de moi",
        "Le chevalier servant protège les lombaires",
        "Il favorise la stabilité et le contact visuel rassurant",
        "Il améliore la qualité relationnelle"
      ],
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
    },
    // Slide 15 - Applications quotidiennes
    {
      type: 'list',
      title: "Au quotidien en crèche",
      items: [
        { term: "Habiller / déshabiller", desc: "Position chevalier servant pour les chaussures, à hauteur de l'enfant" },
        { term: "Jeux au sol", desc: "Chevalier servant plutôt que dos voûté, alterner les positions" },
        { term: "Lit bas", desc: "Un genou au sol, enfant proche du corps, se relever sur la jambe avant" },
        { term: "Au moment du change", desc: "Descendre en chevalier servant, éviter les torsions, anticiper le matériel" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 16 - Balancier intro
    {
      type: 'content',
      title: "Technique du Balancier",
      definition: "Transfert du poids du corps (avant ↔ arrière) pour accompagner le mouvement.",
      content: "Accompagner le mouvement plutôt que tirer avec les bras. Le poids du corps est réparti, le mouvement est contrôlé, le dos ne supporte pas seul la charge.",
      extraInfo: [
        { term: "Utilisation", desc: "Soulever / déplacer un enfant, installation au lit" },
        { term: "Bénéfices", desc: "Effort réduit + moins de contraintes lombaires" }
      ]
    },
    // Slide 17 - Balancier infographie
    {
      type: 'infographic',
      title: "Installer un enfant au lit en protégeant son dos",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/q8tsxbdd_image.png"
    },
    // Slide 18 - Balancier étapes
    {
      type: 'steps',
      title: "Le Balancier - Application au lit à barreaux",
      steps: [
        { num: "1", title: "Se rapprocher", items: ["Maximum du lit", "Ouvrir les barreaux coulissants"], color: "#3B82F6" },
        { num: "2", title: "Positionner", items: ["Un pied légèrement en avant", "Enfant collé au buste"], color: "#8B5CF6" },
        { num: "3", title: "Fléchir", items: ["La jambe d'appui", "Accompagner la descente avec le corps"], color: "#10B981" },
        { num: "4", title: "Poser", items: ["L'enfant doucement", "Sans lâcher brutalement"], color: "#F59E0B" }
      ]
    },
    // Slide 19 - Points vigilance balancier
    {
      type: 'twoColumns',
      title: "Le Balancier - Points de vigilance",
      leftTitle: "À éviter",
      leftItems: [
        "Se pencher bras tendus",
        "Arrondir le dos",
        "Faire une torsion en posant l'enfant",
        "Porter à bout de bras"
      ],
      leftColor: "#EF4444",
      rightTitle: "Toujours",
      rightItems: [
        "Tourner avec les pieds",
        "Garder la charge près du centre de gravité",
        "Utiliser le matériel (barreaux coulissants)"
      ],
      rightColor: "#10B981"
    },
    // Slide 20 - Section Matériel
    {
      type: 'section',
      title: "MATÉRIEL FACILITATEUR",
      subtitle: "Prévention au quotidien",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    // Slide 21 - Plan de change
    {
      type: 'contentLargeImage',
      title: "Matériel : Plan de change",
      content: "L'escalier/marches permet à l'enfant de monter seul, réduisant le portage et favorisant l'autonomie.",
      extraInfo: [
        { term: "Escalier / marches", desc: "L'enfant monte seul → moins de portage + autonomie" },
        { term: "Hauteur adaptée", desc: "Plan de change à bonne hauteur (réglable si possible)" },
        { term: "Préparation", desc: "Tout préparer avant (couches, lingettes, sac, vêtement)" },
        { term: "Zone de dépôt", desc: "Accessible pour éviter de se pencher / tourner" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/f2pe9agw_image.png"
    },
    // Slide 22 - Matériel couchage
    {
      type: 'list',
      title: "Matériel : Couchage et manutention",
      items: [
        { term: "Lits à barreaux coulissants", desc: "Limitent le penchement profond" },
        { term: "Barrières / accès facilités", desc: "Permettent l'approche du bébé" },
        { term: "Chariots / bacs", desc: "À hauteur adaptée pour réduire les flexions" },
        { term: "Roues fonctionnelles + freins", desc: "Réduisent les efforts de poussée/traction" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 23 - Matériel activités sol
    {
      type: 'list',
      title: "Matériel : Activités au sol",
      items: [
        { term: "Sièges au sol / petits sièges", desc: "Se mettre à niveau sans se voûter" },
        { term: "Tabouret à roulettes", desc: "Mobilité + moins d'allers-retours en flexion" },
        { term: "Tapis / espaces organisés", desc: "Limiter les postures contraintes prolongées" },
        { term: "Alterner les positions", desc: "Debout, assis, chevalier servant" }
      ],
      alertColor: "#3B82F6"
    },
    // Slide 24 - Application change
    {
      type: 'content',
      title: "Application : Le change",
      extraInfo: [
        { term: "Risques", desc: "Flexions répétées, torsions, bras tendus" },
        { term: "Prévenir", desc: "Escalier + hauteur adaptée" },
        { term: "Technique", desc: "Chevalier servant pour se mettre à hauteur" },
        { term: "Organisation", desc: "Anticiper le matériel (zéro torsion inutile)" }
      ]
    },
    // Slide 25 - Application couchage
    {
      type: 'content',
      title: "Application : Couchage et activités au sol",
      extraInfo: [
        { term: "Risques", desc: "Penchement profond, postures statiques" },
        { term: "Prévenir", desc: "Barreaux coulissants + proximité" },
        { term: "Technique", desc: "Alterner les postures, utiliser sièges/tabouret" },
        { term: "Règle d'or", desc: "Tourner avec les pieds, pas avec le dos" }
      ]
    },
    // Slide 26 - Intérêt ergonomique
    {
      type: 'list',
      title: "Intérêt ergonomique des techniques",
      items: [
        { term: "Diminue la pression", desc: "Sur les lombaires" },
        { term: "Stabilise le bassin", desc: "Meilleur équilibre" },
        { term: "Réduit les TMS", desc: "Prévention des troubles musculo-squelettiques" },
        { term: "Geste fluide et sécurisé", desc: "Pour vous et l'enfant" },
        { term: "Protection long terme", desc: "Préserve la professionnelle sur la durée" }
      ],
      alertColor: "#10B981"
    },
    // Slide 27 - Rôle AP
    {
      type: 'list',
      title: "Rôle de l'AP : Prévention individuelle et collective",
      items: [
        { term: "Observer et ajuster", desc: "Demander du matériel adapté" },
        { term: "Signaler", desc: "Douleur, matériel défectueux, aménagement inadapté" },
        { term: "Appliquer", desc: "Les techniques au quotidien (exemplarité)" },
        { term: "Favoriser l'autonomie", desc: "De l'enfant quand c'est possible et sécurisé" },
        { term: "Participer", desc: "À l'amélioration continue (réunions, retours d'expérience)" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 28 - Attendus DEAP/VAE
    {
      type: 'list',
      title: "Attendus DEAP / VAE : Ce que le jury veut voir",
      items: [
        { term: "Situation concrète", desc: "Change, portage, coucher, activité au sol" },
        { term: "Identification des risques", desc: "TMS identifiés et analysés" },
        { term: "Techniques mobilisées", desc: "Chevalier servant, balancier, flexion-extension" },
        { term: "Usage du matériel", desc: "Escaliers, barreaux coulissants, tabourets" },
        { term: "Analyse", desc: "Ce que vous avez amélioré + bénéfice pour vous et l'enfant" }
      ],
      alertColor: "#EC4899"
    },
    // Slide 29 - Trame analyse
    {
      type: 'steps',
      title: "Exemple de trame d'analyse",
      steps: [
        { num: "1", title: "Contexte", items: ["Où ? Quand ?", "Avec quel enfant ?"], color: "#3B82F6" },
        { num: "2", title: "Risque repéré", items: ["Posture en cause", "Geste ou matériel"], color: "#EF4444" },
        { num: "3", title: "Action", items: ["Technique choisie", "Aménagement + matériel"], color: "#8B5CF6" },
        { num: "4", title: "Résultat", items: ["Douleur réduite", "Geste plus sûr", "Enfant autonome"], color: "#10B981" },
        { num: "5", title: "Pistes", items: ["Ce que je garde", "Ce que j'améliore", "Alerte si besoin"], color: "#F59E0B" }
      ]
    },
    // Slide 30 - Message final
    {
      type: 'keyMessage',
      title: "Messages clés à retenir",
      messages: [
        "L'ergonomie = adapter le travail au professionnel",
        "Les TMS s'installent progressivement : écoutez les signaux",
        "Chevalier servant : protège le dos, favorise la relation",
        "Balancier : utiliser le poids du corps, pas les bras",
        "Le bon matériel + les bonnes techniques = prévention efficace"
      ],
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    // Slide 31 - Félicitations
    {
      type: 'congratulations',
      title: "Félicitations !",
      message: "Vous avez terminé le cours sur l'Activité 5 : Installation et aide aux déplacements.",
      nextStep: "Testez vos connaissances avec le quiz !",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)"
    }
  ];

  // Quiz questions - 20 questions
  const quizQuestions = [
    {
      id: 1,
      question: "L'ergonomie consiste à :",
      options: [
        "Adapter le professionnel au travail",
        "Adapter le travail au professionnel",
        "Éviter de porter les enfants",
        "Travailler plus vite"
      ],
      correct: 1
    },
    {
      id: 2,
      question: "Les TMS touchent principalement :",
      options: [
        "Le cœur",
        "Les muscles et articulations",
        "Les poumons",
        "Le système digestif"
      ],
      correct: 1
    },
    {
      id: 3,
      question: "En crèche, les zones les plus touchées sont :",
      options: [
        "Lombaires uniquement",
        "Cervicales uniquement",
        "Épaules uniquement",
        "Toutes les réponses (lombaires, cervicales, épaules)"
      ],
      correct: 3
    },
    {
      id: 4,
      question: "Lors du port d'un enfant, il faut :",
      options: [
        "Tendre les bras",
        "Garder l'enfant proche du corps",
        "Arrondir le dos",
        "Tourner le buste"
      ],
      correct: 1
    },
    {
      id: 5,
      question: "La technique du chevalier servant permet :",
      options: [
        "De courir plus vite",
        "De se mettre à hauteur de l'enfant",
        "D'éviter toute flexion",
        "De porter deux enfants"
      ],
      correct: 1
    },
    {
      id: 6,
      question: "Le balancier repose sur :",
      options: [
        "La force des bras",
        "Le transfert du poids du corps",
        "La rapidité",
        "Une flexion du dos"
      ],
      correct: 1
    },
    {
      id: 7,
      question: "L'escalier du plan de change permet :",
      options: [
        "De gagner du temps",
        "De limiter le port de charge",
        "D'éviter la surveillance",
        "De décorer la section"
      ],
      correct: 1
    },
    {
      id: 8,
      question: "Tourner avec le dos chargé augmente :",
      options: [
        "Le confort",
        "Les risques de TMS",
        "La stabilité",
        "L'autonomie"
      ],
      correct: 1
    },
    {
      id: 9,
      question: "Une douleur lombaire répétée doit :",
      options: [
        "Être ignorée",
        "Être prise au sérieux",
        "Disparaître seule",
        "Être cachée"
      ],
      correct: 1
    },
    {
      id: 10,
      question: "Les lits à barreaux coulissants permettent :",
      options: [
        "De réduire les flexions profondes",
        "D'augmenter le portage",
        "D'aller plus vite",
        "De porter à bout de bras"
      ],
      correct: 0
    },
    {
      id: 11,
      question: "Il est préférable de porter un enfant bras tendus :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 1
    },
    {
      id: 12,
      question: "Les TMS apparaissent brutalement :",
      options: [
        "Vrai",
        "Faux - Ils s'installent progressivement"
      ],
      correct: 1
    },
    {
      id: 13,
      question: "Alterner les postures prévient les TMS :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 0
    },
    {
      id: 14,
      question: "Le dos doit rester droit lors d'un port de charge :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 0
    },
    {
      id: 15,
      question: "Le tabouret à roulettes peut améliorer l'ergonomie :",
      options: [
        "Vrai",
        "Faux"
      ],
      correct: 0
    },
    {
      id: 16,
      question: "Le chevalier servant consiste à :",
      options: [
        "Plier le dos",
        "S'accroupir en rond",
        "Poser un genou au sol pour stabiliser la posture",
        "Porter en marchant"
      ],
      correct: 2
    },
    {
      id: 17,
      question: "Le balancier permet :",
      options: [
        "D'utiliser la force des bras",
        "D'utiliser le poids du corps",
        "D'aller plus vite",
        "De soulever bras tendus"
      ],
      correct: 1
    },
    {
      id: 18,
      question: "Après une piqûre d'insecte, on surveille :",
      options: [
        "Les chaussures",
        "Une réaction allergique",
        "La couleur du t-shirt"
      ],
      correct: 1
    },
    {
      id: 19,
      question: "Quel matériel favorise la prévention des TMS ?",
      options: [
        "Table trop basse",
        "Port à bout de bras",
        "Escalier du plan de change, lits à barreaux coulissants, tabouret à roulettes"
      ],
      correct: 2
    },
    {
      id: 20,
      question: "Les principes fondamentaux d'ergonomie sont :",
      options: [
        "Se pencher rapidement et porter bras tendus",
        "Rapprocher la charge, plier les genoux, éviter les rotations",
        "Arrondir le dos pour plus de souplesse"
      ],
      correct: 1
    }
  ];

  const getSlideText = (slide) => {
    let text = '';
    if (slide.title) text += slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle + '. ';
    if (slide.definition) text += slide.definition + ' ';
    if (slide.content) text += slide.content + ' ';
    if (slide.highlight) text += slide.highlight + ' ';
    if (slide.warning) text += 'Attention : ' + slide.warning + ' ';
    if (slide.note) text += slide.note + ' ';
    if (slide.message) text += slide.message + ' ';
    if (slide.nextStep) text += slide.nextStep + ' ';
    
    if (slide.items) {
      slide.items.forEach(item => {
        text += item.term + ': ' + item.desc + '. ';
      });
    }
    if (slide.extraInfo) {
      slide.extraInfo.forEach(info => {
        text += info.term + ': ' + info.desc + '. ';
      });
    }
    if (slide.messages) {
      slide.messages.forEach(msg => {
        text += msg + '. ';
      });
    }
    if (slide.steps) {
      slide.steps.forEach(step => {
        text += step.title + ': ' + step.items.join(', ') + '. ';
      });
    }
    if (slide.sections) {
      slide.sections.forEach(section => {
        text += section.title + ': ' + section.items.join(', ') + '. ';
      });
    }
    if (slide.leftItems) {
      text += 'À éviter: ' + slide.leftItems.join(', ') + '. ';
    }
    if (slide.rightItems) {
      text += 'Toujours: ' + slide.rightItems.join(', ') + '. ';
    }
    
    return text;
  };

  const handleListen = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }

    const slide = slides[currentSlide];
    let textToSpeak = getSlideText(slide);
    
    // Limit text length for faster TTS
    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    // Replace abbreviations
    textToSpeak = textToSpeak
      .replace(/TMS/g, 'troubles musculo-squelettiques')
      .replace(/AP/g, 'auxiliaire de puériculture')
      .replace(/DEAP/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/VAE/g, 'validation des acquis de l\'expérience');

    if (!textToSpeak || textToSpeak.trim() === '') {
      toast.info("Pas de contenu audio pour cette slide");
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin || '';
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      
      setIsLoading(false);
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          toast.error("Erreur de lecture audio");
          setIsPlaying(false);
        };
        audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
          toast.error("Impossible de lire l'audio");
          setIsPlaying(false);
        });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-title"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-center max-w-3xl opacity-90">{slide.subtitle}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-sommaire">
            <h2 className="text-3xl font-bold mb-8 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx}
                  onClick={() => section.slideTarget && goToSlide(section.slideTarget - 1)}
                  className="p-5 rounded-xl cursor-pointer transition-all hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: section.color + '20', borderLeft: `4px solid ${section.color}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: section.color }}
                    >
                      {section.num}
                    </span>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span style={{ color: section.color }}>•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-section"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-4 text-center">{slide.title}</h2>
            {slide.subtitle && <p className="text-xl md:text-2xl opacity-90">{slide.subtitle}</p>}
          </div>
        );

      case 'content':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-content">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-green-400">{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-4">
                {slide.definition && (
                  <div className="p-4 bg-green-500/20 rounded-xl border-l-4 border-green-500">
                    <p className="text-lg font-medium">{slide.definition}</p>
                  </div>
                )}
                {slide.content && <p className="text-gray-300 text-lg leading-relaxed">{slide.content}</p>}
                {slide.warning && (
                  <div className="p-4 bg-red-500/20 rounded-xl border-l-4 border-red-500">
                    <p className="text-red-200 font-medium">{slide.warning}</p>
                  </div>
                )}
                {slide.extraInfo && (
                  <div className="space-y-3 mt-4">
                    {slide.extraInfo.map((info, idx) => (
                      <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                        <span className="font-semibold text-green-300">{info.term} : </span>
                        <span className="text-gray-300">{info.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {slide.image && (
                <div className="lg:w-1/3">
                  <img src={slide.image} alt="" className="rounded-xl shadow-lg w-full h-48 lg:h-64 object-cover" />
                </div>
              )}
            </div>
          </div>
        );

      case 'contentLargeImage':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white overflow-y-auto" data-testid="slide-content-large">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-1/2 space-y-3">
                {slide.definition && (
                  <div className="p-3 bg-green-500/20 rounded-xl border-l-4 border-green-500">
                    <p className="text-base font-medium">{slide.definition}</p>
                  </div>
                )}
                {slide.content && <p className="text-gray-300 text-base leading-relaxed">{slide.content}</p>}
                {slide.extraInfo && (
                  <div className="space-y-2">
                    {slide.extraInfo.map((info, idx) => (
                      <div key={idx} className="p-2 bg-slate-700/50 rounded-lg">
                        <span className="font-semibold text-green-300">{info.term} : </span>
                        <span className="text-gray-300 text-sm">{info.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {slide.image && (
                <div className="lg:w-1/2 flex items-center justify-center">
                  <img src={slide.image} alt="" className="rounded-xl shadow-2xl max-h-[380px] w-auto object-contain" />
                </div>
              )}
            </div>
          </div>
        );

      case 'list':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-list">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: slide.alertColor || '#10B981' }}>{slide.title}</h2>
            <div className="space-y-4">
              {slide.items.map((item, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: (slide.alertColor || '#10B981') + '15', borderLeft: `4px solid ${slide.alertColor || '#10B981'}` }}
                >
                  <span className="font-bold text-lg" style={{ color: slide.alertColor || '#10B981' }}>{item.term}</span>
                  <p className="text-gray-300 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
            {slide.warning && (
              <div className="mt-6 p-4 bg-red-500/20 rounded-xl border border-red-500/50 text-center">
                <p className="text-red-300 font-bold text-lg">{slide.warning}</p>
              </div>
            )}
          </div>
        );

      case 'steps':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-steps">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-green-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slide.steps.map((step, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: step.color + '20', borderTop: `4px solid ${step.color}` }}
                >
                  <div className="mb-4">
                    <span 
                      className="inline-block px-3 py-1 rounded-full text-white font-bold text-sm mb-2"
                      style={{ backgroundColor: step.color }}
                    >
                      {step.num}
                    </span>
                    <h3 className="font-bold text-lg">{step.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {step.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                        <span style={{ color: step.color }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {slide.note && (
              <div className="mt-6 p-4 bg-amber-500/20 rounded-xl border border-amber-500/50">
                <p className="text-amber-200">{slide.note}</p>
              </div>
            )}
          </div>
        );

      case 'infographic':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-4 rounded-2xl text-white flex flex-col items-center justify-center" data-testid="slide-infographic">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">{slide.title}</h2>
            <div className="flex-1 flex items-center justify-center w-full">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="max-h-[450px] w-auto rounded-xl shadow-2xl object-contain" 
              />
            </div>
          </div>
        );

      case 'twoColumns':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-two-columns">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-green-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div 
                className="p-6 rounded-xl"
                style={{ backgroundColor: slide.leftColor + '20', borderTop: `4px solid ${slide.leftColor}` }}
              >
                <h3 className="font-bold text-xl mb-4" style={{ color: slide.leftColor }}>{slide.leftTitle}</h3>
                <ul className="space-y-3">
                  {slide.leftItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: slide.leftColor }} />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div 
                className="p-6 rounded-xl"
                style={{ backgroundColor: slide.rightColor + '20', borderTop: `4px solid ${slide.rightColor}` }}
              >
                <h3 className="font-bold text-xl mb-4" style={{ color: slide.rightColor }}>{slide.rightTitle}</h3>
                <ul className="space-y-3">
                  {slide.rightItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: slide.rightColor }} />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'keyMessage':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-key-message"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.messages.map((msg, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-lg">{msg}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-congratulations"
          >
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            <p className="text-xl text-center max-w-2xl mb-8 opacity-90">{slide.message}</p>
            <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
              <p className="text-lg font-medium">{slide.nextStep}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 p-4 md:p-8" data-testid="quiz-container">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              Quiz - Activité 5 : Ergonomie et TMS
            </h2>
            
            {quizSubmitted && (
              <div className={`mb-6 p-4 rounded-xl text-center ${quizScore >= 14 ? 'bg-green-500/20 border border-green-500' : 'bg-amber-500/20 border border-amber-500'}`}>
                <p className="text-2xl font-bold text-white">
                  Score : {quizScore} / {quizQuestions.length}
                </p>
                <p className={`${quizScore >= 14 ? 'text-green-300' : 'text-amber-300'}`}>
                  {quizScore >= 14 ? 'Excellent travail !' : 'Continuez à réviser !'}
                </p>
              </div>
            )}

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {quizQuestions.map((q, qIdx) => (
                <div key={q.id} className="bg-slate-700/50 rounded-xl p-5">
                  <p className="text-white font-medium mb-4">
                    {qIdx + 1}. {q.question}
                  </p>
                  <div className="grid gap-2">
                    {q.options.map((option, oIdx) => {
                      let buttonClass = "w-full p-3 rounded-lg text-left transition-all ";
                      if (quizSubmitted) {
                        if (oIdx === q.correct) {
                          buttonClass += "bg-green-500/30 border-2 border-green-500 text-green-200";
                        } else if (quizAnswers[q.id] === oIdx && oIdx !== q.correct) {
                          buttonClass += "bg-red-500/30 border-2 border-red-500 text-red-200";
                        } else {
                          buttonClass += "bg-slate-600/50 text-gray-400";
                        }
                      } else {
                        buttonClass += quizAnswers[q.id] === oIdx 
                          ? "bg-green-500/50 border-2 border-green-500 text-white" 
                          : "bg-slate-600/50 hover:bg-slate-600 text-gray-300 border-2 border-transparent";
                      }
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleQuizAnswer(q.id, oIdx)}
                          className={buttonClass}
                          disabled={quizSubmitted}
                          data-testid={`quiz-option-${q.id}-${oIdx}`}
                        >
                          <span className="flex items-center gap-3">
                            {quizSubmitted && oIdx === q.correct && <CheckCircle className="w-5 h-5 text-green-400" />}
                            {quizSubmitted && quizAnswers[q.id] === oIdx && oIdx !== q.correct && <XCircle className="w-5 h-5 text-red-400" />}
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowQuiz(false)}
                className="flex-1 py-3 px-6 bg-slate-600 hover:bg-slate-500 text-white rounded-xl font-medium transition-all"
                data-testid="back-to-course-btn"
              >
                Retour au cours
              </button>
              {!quizSubmitted ? (
                <button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                  className="flex-1 py-3 px-6 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all"
                  data-testid="submit-quiz-btn"
                >
                  Valider ({Object.keys(quizAnswers).length}/{quizQuestions.length})
                </button>
              ) : (
                <button
                  onClick={resetQuiz}
                  className="flex-1 py-3 px-6 bg-green-600 hover:bg-green-500 text-white rounded-xl font-medium transition-all"
                  data-testid="retry-quiz-btn"
                >
                  Recommencer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 p-4 md:p-8" data-testid="presentation-container">
      <div className="max-w-6xl mx-auto">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progression</span>
            <span>{currentSlide + 1} / {slides.length}</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide content */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden min-h-[500px]">
          {renderSlide(slides[currentSlide])}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Précédent
          </button>

          <button
            onClick={handleListen}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
              isPlaying 
                ? 'bg-red-600 hover:bg-red-500' 
                : 'bg-green-600 hover:bg-green-500'
            } text-white`}
            data-testid="listen-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX className="w-5 h-5" />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 className="w-5 h-5" />
                Écouter
              </>
            )}
          </button>

          <button
            onClick={nextSlide}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl transition-all"
            data-testid="next-slide-btn"
          >
            {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite5Presentation;
